import Color from "color";
import { identity, pickBy, some } from "lodash";
import { Descendant, Text } from "slate";
import { jsx } from "slate-hyperscript";

export const parseStyle = (colorString?: string): string | null => {
  if (!colorString || colorString === "transparent") {
    return null;
  }

  let color: Color;
  try {
    color = Color(colorString);
  } catch (error) {
    console.debug(`Could not parse color string: ${colorString}`);
    return null;
  }
  return color.hex();
};

const mergeWithStyle =
  (
    attrs?: Record<string, unknown>
  ): ((el: HTMLElement) => Record<string, unknown>) =>
  (el: HTMLElement) => {
    const { style } = el;
    const styleAttrs = {
      "text-color": parseStyle(style.color),
      "bg-color": parseStyle(style.backgroundColor),
    };
    return pickBy(
      {
        ...styleAttrs,
        ...attrs,
      },
      identity
    );
  };

interface TagFunction {
  (el: HTMLElement): Record<string, unknown>;
}

const ELEMENT_TAGS: Record<string, TagFunction> = {
  A: (el: HTMLElement) => {
    if (el.getAttribute("data-id")) {
      return mergeWithStyle({
        type: "variable",
        id: el.getAttribute("data-id"),
      })(el);
    }
    return mergeWithStyle({ type: "link", url: el.getAttribute("href") })(el);
  },
  BLOCKQUOTE: () => ({ type: "quote" }),
  H1: () => ({ type: "heading-one" }),
  H2: () => ({ type: "heading-two" }),
  H3: () => ({ type: "heading-three" }),
  H4: () => ({ type: "heading-four" }),
  H5: () => ({ type: "heading-five" }),
  H6: () => ({ type: "heading-six" }),
  IMG: (el: HTMLElement) => ({ type: "image", url: el.getAttribute("src") }),
  LI: () => ({ type: "list-item" }),
  OL: () => ({ type: "numbered-list" }),
  P: () => ({ type: "paragraph" }),
  PRE: () => ({ type: "code" }),
  UL: () => ({ type: "bulleted-list" }),
  DIV: () => ({ type: "paragraph" }),
  SPAN: (el: HTMLElement) => {
    if (el.getAttribute("data-id")) {
      return mergeWithStyle({
        type: "variable",
        id: el.getAttribute("data-id"),
      })(el);
    }
    return mergeWithStyle({})(el);
  },
};

// COMPAT: `B` is omitted here because Google Docs uses `<b>` in weird ways.
const TEXT_TAGS: Record<string, TagFunction> = {
  CODE: mergeWithStyle({ code: true }),
  DEL: mergeWithStyle({ strikethrough: true }),
  EM: mergeWithStyle({ italic: true }),
  I: mergeWithStyle({ italic: true }),
  S: mergeWithStyle({ strikethrough: true }),
  STRONG: mergeWithStyle({ bold: true }),
  U: mergeWithStyle({ underline: true }),
  SPAN: mergeWithStyle(),
};

const addAttrsToChildren = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  child: any,
  attrs: Record<string, unknown>
) => {
  if (child.children) {
    // eslint-disable-next-line no-param-reassign
    child.children = child.children.map((item: HTMLElement) => {
      const itemWithAttrs = addAttrsToChildren(item, attrs);
      return { ...itemWithAttrs, ...attrs };
    });
  }
  return child;
};

const deserializeFromDOM = (
  el: HTMLElement
): Descendant | Descendant[] | string => {
  if (el.nodeType === 3) {
    return el.textContent || "";
  }
  if (el.nodeName === "BR") {
    return { text: "\n" };
  }

  // We need to special case handle a div with a single BR, since we use empty
  // paragraph elements to represent this in the new editor.
  if (
    el.nodeName === "DIV" &&
    el.childNodes.length === 1 &&
    el.childNodes[0].nodeName === "BR"
  ) {
    return { type: "paragraph", children: [{ text: "" }] };
  }

  const { nodeName } = el;
  let parent = el;

  if (
    nodeName === "PRE" &&
    el.childNodes[0] &&
    el.childNodes[0].nodeName === "CODE"
  ) {
    parent = el.childNodes[0] as HTMLElement;
  }
  let children = Array.from(parent.childNodes as NodeListOf<HTMLElement>)
    .map(deserializeFromDOM)
    .flat();

  if (children.length === 0) {
    children = [{ text: "" }];
  }

  if (el.nodeName === "BODY") {
    return jsx("fragment", {}, children);
  }

  if (ELEMENT_TAGS[nodeName]) {
    const attrs = ELEMENT_TAGS[nodeName](el);
    if (attrs?.type === "variable") {
      children = [{ text: "" }];
    }
    if (nodeName !== "SPAN" || attrs?.type === "variable") {
      return jsx("element", attrs, children);
    }
  }

  if (TEXT_TAGS[nodeName]) {
    const attrs = TEXT_TAGS[nodeName](el);
    return children.map((child) => {
      if (typeof child === "object" && "children" in child && child?.children) {
        if (child.type === "variable") {
          addAttrsToChildren(child, attrs);
          return {
            ...child,
            ...attrs,
          };
        }
        return addAttrsToChildren(child, attrs);
      }
      return jsx("text", attrs, child);
    });
  }

  return children as Descendant[];
};

const deserialize = (
  htmlString: string,
  wrapIfNecessary = true
): Descendant[] => {
  const document = new DOMParser().parseFromString(htmlString, "text/html");
  const slateContent = deserializeFromDOM(document.body) as Descendant[];
  const needsWrapper = some(slateContent, Text.isText);
  if (needsWrapper && wrapIfNecessary) {
    return [{ type: "paragraph", children: slateContent }];
  }
  return slateContent;
};

export default deserialize;
